/**
 * Group activity sessions in the Edit page by their seriesId.
 *
 * PRD docs/prd-repeated-sessions.md §5: the Edit form keeps the existing
 * upcoming/past split (handled by the caller). Within each region, sessions that
 * share a non-empty seriesId render under a single banner showing frequency +
 * count + date range. Sessions without a seriesId (or with a non-empty seriesId
 * but only one occurrence visible in this region) are rendered ungrouped so that
 * a half-past, half-upcoming series shows correctly in both sections.
 *
 * Summary frequency heuristic per PRD §5.6:
 *   ~1 day  → Daily
 *   ~7 days → Weekly
 *   29–32 days → Monthly
 *   else (incl. 14, 21, 28) → Custom series
 * It is decorative; out-of-pattern sessions edited individually may produce a
 * slightly misleading label but never affect business logic.
 */

import { ptInstantParts } from './timezone';

export interface IndexedSession<S> {
  session: S;
  index: number;
}

export type Grouped<S> =
  | { kind: 'single'; entry: IndexedSession<S> }
  | { kind: 'series'; seriesId: string; entries: IndexedSession<S>[]; summary: SeriesSummary };

export type SeriesFrequency = 'Daily' | 'Weekly' | 'Monthly' | 'Custom';

export interface SeriesSummary {
  count: number;
  frequency: SeriesFrequency;
  firstStartISO: string;
  lastStartISO: string;
}

interface SessionLike {
  startTime: string;
  seriesId?: string | null;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Group a list of sessions by seriesId. Preserves the original index of each session
 * (so callers can map back to onSessionChange/onRemoveSession handlers in the parent).
 *
 * - Series with ≥ 2 entries in this region produce a "series" group.
 * - Series with only 1 entry in this region (e.g. the rest are in the other time section)
 *   render ungrouped — a singleton banner adds noise without value.
 * - Sessions with null/empty seriesId always render ungrouped.
 *
 * Output order: the same chronological order as the input. Each entry's first appearance
 * determines where its group is placed in the output.
 */
export function groupSessionsBySeriesId<S extends SessionLike>(
  entries: IndexedSession<S>[]
): Grouped<S>[] {
  const groups = new Map<string, IndexedSession<S>[]>();
  for (const e of entries) {
    const sid = e.session.seriesId;
    if (sid) {
      const list = groups.get(sid);
      if (list) list.push(e);
      else groups.set(sid, [e]);
    }
  }

  const seriesInsertedAt = new Set<string>();
  const out: Grouped<S>[] = [];
  for (const e of entries) {
    const sid = e.session.seriesId;
    if (sid && (groups.get(sid)?.length ?? 0) > 1) {
      if (seriesInsertedAt.has(sid)) continue;
      seriesInsertedAt.add(sid);
      const list = groups.get(sid)!;
      out.push({
        kind: 'series',
        seriesId: sid,
        entries: list,
        summary: summarizeSeries(list.map((x) => x.session)),
      });
    } else {
      out.push({ kind: 'single', entry: e });
    }
  }
  return out;
}

function summarizeSeries<S extends SessionLike>(sessions: S[]): SeriesSummary {
  const sorted = [...sessions].sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
  );
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  return {
    count: sorted.length,
    frequency: detectFrequency(sorted),
    firstStartISO: first.startTime,
    lastStartISO: last.startTime,
  };
}

function detectFrequency(sortedAsc: SessionLike[]): SeriesFrequency {
  if (sortedAsc.length < 2) return 'Custom';
  const a = new Date(sortedAsc[0].startTime).getTime();
  const b = new Date(sortedAsc[1].startTime).getTime();
  const days = Math.abs(b - a) / MS_PER_DAY;
  if (days >= 0.95 && days <= 1.05) return 'Daily';
  if (days >= 6.5 && days <= 7.5) return 'Weekly';
  if (days >= 29 && days <= 32) return 'Monthly';
  return 'Custom';
}

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** Format an ISO instant as "MMM d, YYYY" in PT. */
export function formatPtMonthDayYear(iso: string): string {
  const p = ptInstantParts(new Date(iso));
  return `${MONTHS_SHORT[p.month - 1]} ${p.day}, ${p.year}`;
}

/** Format the banner caption like "Weekly series · 8 sessions · May 19 → Jul 7, 2026". */
export function describeSeries(summary: SeriesSummary): string {
  const range = `${formatPtMonthDayYear(summary.firstStartISO)} to ${formatPtMonthDayYear(summary.lastStartISO)}`;
  return `${summary.frequency} series · ${summary.count} session${summary.count === 1 ? '' : 's'} · ${range}`;
}
